/**
 * Patient Detail Page
 * ====================
 * View patient information and chart history.
 */

import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  FileText,
  Edit,
  Trash2,
  AlertTriangle,
  Calendar,
  Phone,
  Mail,
  MapPin,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { getPatient, deletePatient, getPatientCharts } from '@/api/patients';
import { useCreateChart } from '@/hooks/useChart';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { formatDate, calculateAge, getRiskLevelClass, getChartStatusClass } from '@/lib/utils';
import { useState } from 'react';

export default function PatientDetail() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: patient, isLoading, error } = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => getPatient(patientId!, true),
    enabled: !!patientId,
  });

  const { data: charts } = useQuery({
    queryKey: ['patient-charts', patientId],
    queryFn: () => getPatientCharts(patientId!),
    enabled: !!patientId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePatient(patientId!),
    onSuccess: () => {
      toast.success('Patient deleted');
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      navigate('/patients');
    },
    onError: () => {
      toast.error('Failed to delete patient');
    },
  });

  const createChartMutation = useCreateChart();

  const handleCreateNewChart = async () => {
    if (!patientId) return;
    
    try {
      const newChart = await createChartMutation.mutateAsync({
        patient_id: patientId,
        exam_date: new Date().toISOString().split('T')[0],
      });
      navigate(`/charts/${newChart.id}`);
    } catch (error) {
      // Error is already handled by the mutation
      console.error('Failed to create chart:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Patient not found</h2>
        <p className="text-gray-600 mb-4">The patient you're looking for doesn't exist.</p>
        <Link to="/patients">
          <Button>Back to Patients</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/patients" className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {patient.full_name}
            </h1>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {patient.chart_number && <span>Chart #{patient.chart_number}</span>}
              {patient.date_of_birth && (
                <span>Age {calculateAge(patient.date_of_birth)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleCreateNewChart} isLoading={createChartMutation.isPending}>
            <Plus className="w-4 h-4 mr-2" />
            New Chart
          </Button>
          <Link to={`/patients/${patientId}/edit`}>
            <Button variant="outline">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Info */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Patient Information
            </h2>
            <div className="space-y-4">
              {patient.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${patient.email}`} className="text-primary-600 hover:underline">
                    {patient.email}
                  </a>
                </div>
              )}
              {patient.phone_primary && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:+${patient.mobile_code || ''}${patient.phone_primary}`} className="text-primary-600 hover:underline">
                    {patient.mobile_code ? `+${patient.mobile_code} ` : ''}{patient.phone_primary}
                  </a>
                </div>
              )}
              {patient.date_of_birth && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{formatDate(patient.date_of_birth)}</span>
                </div>
              )}
              {(patient.city || patient.state) && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>
                    {[patient.city, patient.state].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Risk Assessment */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Risk Assessment
            </h2>
            {patient.periodontal_risk_level ? (
              <div className="space-y-3">
                <Badge className={getRiskLevelClass(patient.periodontal_risk_level)}>
                  {patient.periodontal_risk_level.charAt(0).toUpperCase() +
                    patient.periodontal_risk_level.slice(1)}{' '}
                  Risk
                </Badge>
                {patient.periodontal_risk_notes && (
                  <p className="text-sm text-gray-600">
                    {patient.periodontal_risk_notes}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No risk assessment recorded</p>
            )}

            {patient.referred_to_periodontist && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Badge variant="warning">Referred to Periodontist</Badge>
                {patient.referral_date && (
                  <p className="text-sm text-gray-600 mt-2">
                    Referred on {formatDate(patient.referral_date)}
                  </p>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Chart History */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Chart History
              </h2>
              {patient.last_perio_exam_date && (
                <span className="text-sm text-gray-500">
                  Last exam: {formatDate(patient.last_perio_exam_date)}
                </span>
              )}
            </div>

            {charts && charts.length > 0 ? (
              <div className="space-y-3">
                {charts.map((chart) => (
                  <Link
                    key={chart.id}
                    to={`/charts/${chart.id}`}
                    className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileText className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {chart.exam_type || 'Periodontal Exam'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatDate(chart.exam_date)} • Version {chart.version}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {chart.is_baseline && (
                        <Badge variant="secondary">Baseline</Badge>
                      )}
                      <Badge className={getChartStatusClass(chart.status)}>
                        {chart.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 mb-4">No charts yet</p>
                <Button onClick={handleCreateNewChart} isLoading={createChartMutation.isPending}>
                  Create First Chart
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Patient"
      >
        <div className="p-4">
          <p className="text-gray-600 mb-4">
            Are you sure you want to delete <strong>{patient.full_name}</strong>?
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteMutation.mutate()}
              isLoading={deleteMutation.isPending}
            >
              Delete Patient
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
